import { twMerge } from "tailwind-merge";
import type { VoyagePerformanceReport } from "../models/voyage-evaluation.models";
import {
  formatVoyageEvaluationDate,
  formatVoyageEvaluationNumber,
  isSafeVoyageEvaluationLink,
} from "../utils/voyage-evaluation-selectors.utils";

interface Props {
  reports: VoyagePerformanceReport[];
  selectedReport: VoyagePerformanceReport | null;
  onSelect: (report: VoyagePerformanceReport | null) => void;
}

const getGradientColor = (value: number | null, values: Array<number | null>): string | undefined => {
  if (value === null) return undefined;

  const populated = values.filter((candidate): candidate is number => candidate !== null && Number.isFinite(candidate));
  const minimum = Math.min(...populated);
  const maximum = Math.max(...populated);
  const ratio = maximum === minimum ? 0.5 : (value - minimum) / (maximum - minimum);

  return `rgba(34, 197, 94, ${0.08 + ratio * 0.3})`;
};

// Recreates the report-grain table, its row-selection interaction, and the three PBIP gradients.
const VoyagePerformanceReportTable = ({ reports, selectedReport, onSelect }: Props) => {
  const draftValues = reports.map((report) => report.draft);
  const totalConsumptionValues = reports.map((report) => report.totalConsumption24h);
  const rpmValues = reports.map((report) => report.mainEngineRpm);

  return <section
    className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
    data-test="voyage-performance-report-table"
  >
    <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
      <div>
        <h2 className="text-sm font-semibold">Filtered Voyage Data</h2>
        <p className="text-[10px] text-gray-500">Select a row to cross-filter the performance summaries</p>
      </div>
      <span className="text-xs text-gray-500">{reports.length} matching reports</span>
    </div>
    <div className="max-h-[620px] overflow-auto">
      <table className="w-full min-w-[2350px] text-[11px]">
        <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-900">
          <tr>
            {["Report date", "Voyage", "Condition", "Port", "Steaming hours", "Calculated STW", "ME cons / 24h", "Mid draft", "Aux cons / 24h", "Total cons / 24h", "SOG", "Good weather", "Wind Beaufort", "Combined Wave Height (m)", "Current factor", "Noon wind force", "Noon swell", "ME RPM", "ME Power (kW)", "SFOC", "Slip %", "Form"].map((heading) => (
              <th key={heading} className="whitespace-nowrap border-b border-gray-200 px-3 py-3 font-semibold dark:border-gray-700">{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reports.map((report) => {
            const selected = selectedReport?.formGuid === report.formGuid;

            return (
              <tr
                key={report.formGuid}
                onClick={() => onSelect(selected ? null : report)}
                data-test={`voyage-performance-report-row-${report.formGuid}`}
                className={twMerge(
                  "cursor-pointer odd:bg-white even:bg-gray-50/60 hover:bg-primary-10 dark:odd:bg-gray-800 dark:even:bg-gray-800/70",
                  selected && "ring-1 ring-inset ring-green",
                )}
              >
                <td className="whitespace-nowrap border-b px-3 py-2 dark:border-gray-700">{formatVoyageEvaluationDate(report.reportDate)}</td>
                <td className="border-b px-3 py-2 dark:border-gray-700">{report.voyage}</td>
                <td className="border-b px-3 py-2 font-semibold dark:border-gray-700">{report.condition ?? "—"}</td>
                <td className="border-b px-3 py-2 dark:border-gray-700">{report.port ?? "—"}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.steamingHours, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.calculatedStw, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.totalMeConsumption24h, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700" style={{ backgroundColor: getGradientColor(report.draft, draftValues) }}>{formatVoyageEvaluationNumber(report.draft, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.totalAuxConsumption24h, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700" style={{ backgroundColor: getGradientColor(report.totalConsumption24h, totalConsumptionValues) }}>{formatVoyageEvaluationNumber(report.totalConsumption24h, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.sog, 2)}</td>
                <td className="border-b px-3 py-2 dark:border-gray-700">{report.goodWeather === null ? "—" : report.goodWeather ? "Yes" : "No"}</td>
                {[report.windBeaufort, report.primaryWaveHeight, report.currentFactor, report.noonReportWindForce, report.noonReportSwell].map((value, index) => <td key={index} className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(value, 2)}</td>)}
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700" style={{ backgroundColor: getGradientColor(report.mainEngineRpm, rpmValues) }}>{formatVoyageEvaluationNumber(report.mainEngineRpm, 2)}</td>
                <td className="border-b px-3 py-2 text-gray-500 dark:border-gray-700">Unavailable</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.sfoc, 2)}</td>
                <td className="border-b px-3 py-2 tabular-nums dark:border-gray-700">{formatVoyageEvaluationNumber(report.slipPercent, 2)}</td>
                <td className="border-b px-3 py-2 dark:border-gray-700">
                  {isSafeVoyageEvaluationLink(report.formLink) ? <a href={report.formLink!} onClick={(event) => event.stopPropagation()} target="_blank" rel="noreferrer" className="font-semibold text-green underline">Open</a> : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!reports.length && <p className="p-10 text-center text-sm text-gray-500" data-test="voyage-performance-report-empty">No reports match the selected filters.</p>}
    </div>
  </section>;
};

export default VoyagePerformanceReportTable;
